import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import { useSelector } from "react-redux";
import { AppState } from '../store/rootReducer';
import { DecodedVehicleInformation } from '../Data/DecodedVehicleInformation';
import { isNil } from 'ramda';
import { useDispatch } from 'react-redux';
import { Constants } from '../utils/constants';
import { Input } from '@progress/kendo-react-inputs';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import {hasValue, LookupItem} from "@amsanalytics/react-shared-library";
import {CreateVehicle} from "../Interfaces/Vehicle/CreateVehicle";
import {CAR_TAG_NO_MAX_LENGTH, getColorMaxLength} from "../utils/vehicleFieldLimits";
import {useConfiguration} from "../Data/ConfigurationData";
import {
  AmsLookupForComboBoxDisplay, getAmsLocationsOfSaleToDisplay,
  getAmsLookupsToDisplay
} from "./Views/OrderEditor/AmsLoanPostingDialog/AmsLoanPostingUtils";

export interface VehicleAddNewDialogProps {
  onClose: () => void;
  onSaveVehicle: (vehicleToAdd: CreateVehicle) => void;
  vehicleOrganizationId: string;
  generatedStockNumber?: string;
}

export default function VehicleAddNewDialog(props: VehicleAddNewDialogProps) {
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const organizations = lookups.organizations;
  const bookoutSources = lookups.bookoutSources;
  const locationsOfPurchaseToDisplay = getAmsLookupsToDisplay(lookups.vehicleLocationsOfPurchase);
  const locationsOfSaleToDisplay = getAmsLocationsOfSaleToDisplay(organizations.filter(o => hasValue(o.amsCode)));
  const [isDecodingVin, setIsDecodingVin] = useState<boolean>(false);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { data: configuration } = useConfiguration();
  const [newVehicle, setNewVehicle] = useState<CreateVehicle>({
    carStatus: 'AV',
    stockNumber: props.generatedStockNumber,
    organizationId: props.vehicleOrganizationId,
    locationOfPurchaseId: configuration?.clientHasDbSyncUrl
    ? locationsOfPurchaseToDisplay?.find(loc => loc.code.toUpperCase() === 'UN')?.id
    : undefined,
  });

  // Try to get vehicle information from VIN entered by user. This occurs when user clicks the 'Decode VIN' button in the app form.
  const getVehicleInformationByVin = async () => {
    try {
      setIsDecodingVin(true);
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${newVehicle.vin}?format=json`);
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      // Pluck out the year, make, and model from the response
      const vinInfo: { Results: { Value: string, ValueId: string, Variable: string, VariableId: number }[] } = await response.json();
      // Determine if there was an error decoding the VIN
      const hasError = vinInfo?.Results.find(r => r.VariableId === 143)?.Value !== "0";

      // If there was no error, update the year, make, and model
      if (!hasError) {
        const yearResult = vinInfo.Results.find(r => r.VariableId === 29)?.Value;
        const make: string = vinInfo.Results.find(r => r.VariableId === 26)?.Value as string;
        const model: string = vinInfo.Results.find(r => r.VariableId === 28)?.Value as string;
        const year: number = yearResult ? parseInt(yearResult) : 0;
        return { year, make, model };
      } else {
        // If there was an error, show a snackbar with the error message
        return undefined;
      }
    } catch (error: any) {
      // catch any errors and show a snackbar with the error message
      return undefined;
    }
  }

  const handleDecodeVinClick = async () => {
    await getVehicleInformationByVin().then((decodedInfo: DecodedVehicleInformation | undefined) => {
      if (!isNil(decodedInfo)) {
        setNewVehicle({
          ...newVehicle,
          year: decodedInfo?.year,
          make: decodedInfo?.make,
          model: decodedInfo?.model,
        })
        dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'VIN decoded successfully.', isError: false } });
      } else {
        // Show snackbar with error message
        dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'VIN could not be decoded. Please double-check the VIN and try again.', isError: false } });
      }
    }).finally(() => {
      setIsDecodingVin(false);
    });
  };


  function isBlank(str: any): boolean {
    return (!str || /^\s*$/.test(str));
  }

  // Returns undefined for a cleared/unparseable input so the field never holds NaN
  function parseAmount(value: string): number | undefined {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : Math.abs(parsed);
  }

  function disableSave() {
    if (newVehicle.year && newVehicle.year < 1980) {
      return true;
    }

    if (newVehicle.year === null || newVehicle.year === undefined || isNaN(newVehicle.year!) || isBlank(newVehicle.year)) {
      return true;
    }

    if (disableSaveButton) {
      return true
    }

    if (!hasValue(newVehicle.stockNumber)) {
      return true
    }

    if (configuration?.clientHasDbSyncUrl && !hasValue(newVehicle.locationOfPurchaseId)) {
      return true;
    }

    return false;
  }

  return (
    <Dialog
      open={true}
      onClose={() => props.onClose()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Add New Vehicle</DialogTitle>

      <DialogContent>

        <div className='vehicleDialog' style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '370px' }}>
          <div style={{display: 'flex', flex: 1, alignItems: 'flex-end', justifyContent: 'space-between'}}>
            <Input
              label="VIN #"
              style={{width: '300px'}}
              value={newVehicle?.vin || ''}
              onChange={(e) => newVehicle && setNewVehicle({
                ...newVehicle,
                vin: e.target.value as string
              })}

            />

            <Button
              variant="outlined"
              color="primary"
              style={{
                height: '40px',
                textTransform: 'none',
                // flex: 1,
              }}
              onClick={() => handleDecodeVinClick()}
              disabled={!hasValue(newVehicle.vin) || isDecodingVin === true}
            >
              <>
                {isDecodingVin === true && <CircularProgress size={20} style={{ marginRight: '10px' }} />}
                {isDecodingVin !== true && <ContentPasteSearchIcon style={{ marginRight: '5px' }} />}
              </>
              Decode
            </Button>
          </div>
          <Input
            label="Stock #"
            value={newVehicle?.stockNumber || ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              stockNumber: e.target.value as string ?? undefined
            })}
          />
          <Input
            label="Make"

            value={newVehicle?.make || ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              make: e.target.value as string
            })}
          />
          <Input
            label="Model"

            value={newVehicle?.model || ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              model: e.target.value as string
            })}
          />
          <Input
            label="Year (required)"

            value={newVehicle?.year || undefined}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              year: parseFloat(e.target.value as string)
            })}
          />
          <Input
            label="Color"
            value={newVehicle?.color || ''}
            maxLength={getColorMaxLength(configuration?.clientHasDbSyncUrl)}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              color: e.target.value as string
            })}
          />
          <Input
            label="Tag #"
            value={newVehicle?.carTagNo || ''}
            maxLength={CAR_TAG_NO_MAX_LENGTH}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              carTagNo: e.target.value as string
            })}
          />
          <Input
            label="Type"

            value={newVehicle?.body || ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              body: e.target.value as string
            })}
          />
          <Input
            label="Miles"
            value={newVehicle?.mileage ?? ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              mileage: parseAmount(e.target.value as string)
            })}
          />
          <Input
            label="ACV"

            value={newVehicle?.acv ?? ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              acv: parseAmount(e.target.value as string)
            })}
          />
          <Input
            label="Bookout"

            value={newVehicle?.bookoutValue ?? ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              bookoutValue: parseAmount(e.target.value as string)
            })}
          />
          <ComboBox
            label="Bookout Source"
            data={bookoutSources}
            textField="name"
            value={bookoutSources.find(bs => bs.id == newVehicle?.bookoutSourceId)}
            onChange={(e) => {
              newVehicle && setNewVehicle({
                ...newVehicle,
                bookoutSourceId: (e.target.value as LookupItem)?.id,
                otherBookoutSource: (e.target.value as LookupItem)?.id?.toLowerCase() !== Constants.bookoutSources.other ? undefined : newVehicle.otherBookoutSource,
              })
            }}
          />
          <Input
            label="Other Bookout Source"
            disabled={newVehicle?.bookoutSourceId?.toLowerCase() !== Constants.bookoutSources.other}
            value={newVehicle?.otherBookoutSource ?? undefined}
            maxLength={100}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              otherBookoutSource: e.target.value as string
            })}
          />
          <Input
            label="Price"

            value={newVehicle?.carPrice ?? ''}
            onChange={(e) => newVehicle && setNewVehicle({
              ...newVehicle,
              carPrice: parseAmount(e.target.value as string)
            })}
          />
          <ComboBox
            label="Location of Sale"
            data={locationsOfSaleToDisplay.sort((a, b) => a.description.localeCompare(b.description))}
            textField="description"
            value={locationsOfSaleToDisplay.find(o => o.id.toUpperCase() === newVehicle?.organizationId?.toUpperCase())}
            onChange={(e) => {
              newVehicle && setNewVehicle({
                ...newVehicle,
                organizationId: (e.target.value as AmsLookupForComboBoxDisplay)?.id,
              })
            }}
          />
          {configuration?.clientHasDbSyncUrl &&
            <ComboBox
              label="Location of Purchase"
              data={locationsOfPurchaseToDisplay.sort((a, b) => a.description.localeCompare(b.description))}
              textField="description"
              value={locationsOfPurchaseToDisplay.find(lop => lop.id.toUpperCase() === newVehicle?.locationOfPurchaseId?.toUpperCase())}
              onChange={(e) => {
                newVehicle && setNewVehicle({
                  ...newVehicle,
                  locationOfPurchaseId: (e.target.value as AmsLookupForComboBoxDisplay)?.id,
                })
              }}
            />
          }
        </div>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => props.onClose()}
          color="inherit"
          autoFocus
        >
          Cancel
        </Button>
        {newVehicle && (
          <Button
            color="error"
            disabled={disableSave()}
            onClick={async () => {
              setDisableSaveButton(true)
              props.onSaveVehicle({
                ...newVehicle,
                organizationId: props.vehicleOrganizationId
              });
              setDisableSaveButton(false)
            }}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
